import { z } from 'zod';
import { CAMPAIGN_STATUSES, CAMPAIGN_TYPES } from '../enums.js';
import {
  optionalDate,
  optionalNumber,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import { businessManagerFilterSchema, type BusinessManagerRef } from './records.js';

const money = z.coerce.number().min(0).max(1e15);
const count = z.coerce.number().int().min(0).max(100_000_000);

export const createCampaignSchema = z
  .object({
    name: z.string().trim().min(1, 'Required').max(200),
    type: z.enum(CAMPAIGN_TYPES).optional(),
    status: z.enum(CAMPAIGN_STATUSES).default('PLANNING'),
    // `optionalDate`, not `z.iso.date().optional()`: an empty date input
    // submits '', which the latter rejects — so a campaign could not be saved
    // with its dates left blank.
    startDate: optionalDate(),
    endDate: optionalDate(),

    // Likewise `optionalNumber`: a blank number input submits '', which
    // `z.coerce.number()` turns into a spurious 0.
    expectedRevenue: optionalNumber(money),
    budgetedCost: optionalNumber(money),
    actualCost: optionalNumber(money),
    expectedResponse: optionalNumber(count),
    numbersSent: optionalNumber(count),

    description: optionalText(4000),
    businessManagerId: uuidSchema.optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((input) => !input.startDate || !input.endDate || input.endDate >= input.startDate, {
    // A campaign that ends before it starts makes every duration report
    // negative, and the mistake is invisible until someone runs one.
    message: 'The end date cannot be before the start date',
    path: ['endDate'],
  });
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

export const updateCampaignSchema = toPatchSchema(createCampaignSchema);
export type UpdateCampaignInput = z.infer<typeof updateCampaignSchema>;

export const CAMPAIGN_SORT_FIELDS = [
  'createdAt',
  'updatedAt',
  'name',
  'startDate',
  'endDate',
  'actualCost',
] as const;

export const listCampaignsQuerySchema = paginationQuerySchema.extend({
  sortBy: z.enum(CAMPAIGN_SORT_FIELDS).default('startDate'),
  businessManagerId: businessManagerFilterSchema.optional(),
  status: z.enum(CAMPAIGN_STATUSES).optional(),
  type: z.enum(CAMPAIGN_TYPES).optional(),
});
export type ListCampaignsQuery = z.infer<typeof listCampaignsQuerySchema>;

export interface CampaignSummary {
  id: string;
  name: string;
  type: (typeof CAMPAIGN_TYPES)[number] | null;
  status: (typeof CAMPAIGN_STATUSES)[number];
  startDate: string | null;
  endDate: string | null;
  expectedRevenue: string | null;
  actualCost: string | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignDetail extends CampaignSummary {
  budgetedCost: string | null;
  expectedResponse: number | null;
  numbersSent: number | null;
  description: string | null;
  customFields: Record<string, unknown>;
  /**
   * What the campaign actually produced. Computed on read: a campaign has few
   * enough attributed records that the aggregate is cheap and always current,
   * where a denormalised counter drifts the first time a deal is reassigned.
   */
  stats: {
    leadCount: number;
    dealCount: number;
    wonDealCount: number;
    /** Won revenue attributed to this campaign. */
    wonValue: string;
    pipelineValue: string;
    /** Won value minus actual cost, or null when no cost is recorded. */
    roi: string | null;
  };
}
